import { FlexRow } from '@fox/layout';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import MuiTextField, { TextFieldProps } from '@mui/material/TextField';
import { CSSProperties, PropsWithChildren, ReactNode, Ref, forwardRef } from 'react';
import styled from 'styled-components';

export type TextFieldStatus = 'warning' | 'error' | 'success' | undefined;
export type ExtendedTextFieldProps = TextFieldProps & {
	/** When true will render a standard-variant TextField with a static value.
	 * @default undefined
	 */
	readOnly?: boolean;
	/** An element to be rendered at the start of the input, such as an icon or button.
	 * @default undefined
	 */
	startAdornment?: ReactNode;
	/** An element to be rendered at the end of the input, such as an icon or button.
	 * @default undefined
	 */
	endAdornment?: ReactNode;
	/** Optional styles for the end adornment
	 * @default undefined
	 */
	endAdornmentStyle?: CSSProperties;
	/** Visually indicates the _'success'_, _'warning'_ or _'error'_ status of a TextField, by changing the color and adding an end adornment.
	 * @default undefined
	 */
	status?: TextFieldStatus;
	/** Hides the input's validation icons.
	 * @default undefined
	 */
	hideValidationIcon?: boolean;
	/** Hides the input's underline. Only applicable if readOnly is true.
	 * @default undefined
	 */
	disableUnderline?: boolean;
	/** A function that can be called when a textfield is clearable.
	 * @default undefined
	 */
	onClear?: () => void;
};

// End adornments for status
const ValidationIcon = ({ status }: { status: string }) => {
	if (!status) return <></>;
	switch (status) {
		case 'error':
			return <ErrorIcon color="error" />;
		case 'warning':
			return <WarningIcon color="warning" />;
		case 'success':
			return <CheckCircleIcon color="success" />;
	}
};

// CSS rules to color inputs even when not in foucs. Focusing on an input will still be indicated by a thicker, darker border.
const StyledTextField = styled(MuiTextField)<{ status?: TextFieldStatus | 'primary' }>(
	({ status, theme }) => `
	& .MuiOutlinedInput-notchedOutline {
		border-color: ${status ? theme.palette[status].main : 'undefined'};
	}
	&:hover .MuiOutlinedInput-notchedOutline {
		border-color: ${status ? theme.palette[status].main : 'undefined'};
	}
	& .MuiInputLabel-root {
		color: ${status ? theme.palette[status].main : 'undefined'};
	}
	& .MuiFormHelperText-root {
		color: ${status ? theme.palette[status].main : 'undefined'};
	}
`
);
/**
 * This is an extension of the Mui TextField with common implementations such as visual input validation and start/end adornments built in.
 */
export const TextField = forwardRef((props: PropsWithChildren<ExtendedTextFieldProps>, ref: Ref<HTMLDivElement>) => {
	const {
		endAdornment,
		endAdornmentStyle,
		status,
		startAdornment,
		color,
		readOnly,
		InputProps,
		error,
		hideValidationIcon,
		disableUnderline,
		variant = 'outlined',
		...rest
	} = props;

	const endAdornments = (
		<FlexRow style={{ alignItems: 'center', gap: '0.5rem', overflow: 'visible', ...(endAdornmentStyle ?? {}) }}>
			{InputProps?.endAdornment}
			{endAdornment}
			{hideValidationIcon || !status ? null : <ValidationIcon status={error ? 'error' : status} />}
		</FlexRow>
	);

	const allInputProps = {
		...InputProps,
		readOnly: readOnly,
		endAdornment: endAdornments,
		startAdornment: startAdornment
	};

	// allow disabledUnderline to be set ONLY if input is readOnly
	if (disableUnderline && readOnly) allInputProps['disableUnderline'] = true;

	return (
		<StyledTextField
			ref={ref}
			variant={readOnly ? 'standard' : variant}
			InputProps={allInputProps}
			status={error ? 'error' : status}
			color={error ? 'error' : status ?? color}
			{...rest}
		/>
	);
});
